/* PPE Parts & Supply: one backdrop colour behind every product photo.
   The catalog's photos were shot on white, grey, green, blue and teal. This
   repaints each photo's backdrop in the browser to the --photo-backdrop colour
   (base.css), leaving the image files in Shopify untouched.

   How a photo is read:
   - Its corners give the backdrop colour. Corners that disagree mean the photo
     fills the frame, and it is left alone.
   - A coloured backdrop is replaced wherever that colour appears, keeping the
     shading so soft shadows survive. Grey and black parts are not affected.
   - A white or grey backdrop is replaced from the edges inwards only, so white
     and grey areas inside the product stay as they are. A white or grey
     backdrop that fades across the photo is left alone.
   A photo that cannot be read (another domain, a decode error) is shown as is. */
(function () {
  'use strict';

  const SELECTOR = [
    '.product-card__media img',
    '.product-gallery__slide img',
    '.product-gallery__thumb img',
    '.quick-view-product__media img',
    '.predictive-search__thumb img',
    '.cart-toast__media img',
    '.cart-line__media img',
    '.cart-drawer__media img'
  ].join(',');
  const MAX_SIDE = 1400;
  const root = document.documentElement;

  const readTarget = () => {
    const value = getComputedStyle(root).getPropertyValue('--photo-backdrop').trim();
    const hex = /^#([0-9a-f]{6})$/i.exec(value);
    if (!hex) return [247, 247, 247];
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const TARGET = readTarget();

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context || !canvas.toBlob) return;

  /* Average of a 3x3 patch, or null when the patch is see-through. */
  const patch = (data, width, x0, y0) => {
    const sum = [0, 0, 0];
    for (let y = y0; y < y0 + 3; y += 1) {
      for (let x = x0; x < x0 + 3; x += 1) {
        const i = (y * width + x) * 4;
        if (data[i + 3] < 250) return null;
        sum[0] += data[i]; sum[1] += data[i + 1]; sum[2] += data[i + 2];
      }
    }
    return sum.map((value) => value / 9);
  };

  const gap = (a, b) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));

  /* The colour at least three corners share, or null. */
  const backdropOf = (data, width, height) => {
    const corners = [
      patch(data, width, 1, 1), patch(data, width, width - 4, 1),
      patch(data, width, 1, height - 4), patch(data, width, width - 4, height - 4)
    ];
    if (corners.includes(null)) return null;
    let best = [];
    corners.forEach((corner) => {
      const near = corners.filter((other) => gap(corner, other) < 26);
      if (near.length > best.length) best = near;
    });
    if (best.length < 3) return null;
    return [0, 1, 2].map((c) => best.reduce((sum, corner) => sum + corner[c], 0) / best.length);
  };

  const replaceColoured = (data, bg, spread) => {
    const bgMean = (bg[0] + bg[1] + bg[2]) / 3;
    const b0 = bg[0] - bgMean; const b1 = bg[1] - bgMean; const b2 = bg[2] - bgMean;
    const near = 0.2 * spread; const far = 0.5 * spread;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]; const g = data[i + 1]; const b = data[i + 2];
      const mean = (r + g + b) / 3;
      const d = Math.max(Math.abs(r - mean - b0), Math.abs(g - mean - b1), Math.abs(b - mean - b2));
      if (d >= far) continue;
      const keep = d <= near ? 0 : (d - near) / (far - near);
      const shade = Math.min(1.03, mean / bgMean);
      data[i] = Math.min(255, TARGET[0] * shade) * (1 - keep) + r * keep;
      data[i + 1] = Math.min(255, TARGET[1] * shade) * (1 - keep) + g * keep;
      data[i + 2] = Math.min(255, TARGET[2] * shade) * (1 - keep) + b * keep;
    }
  };

  /* Share of the edge pixels within `near` of the backdrop. An evenly lit
     backdrop scores close to 1; one that fades from light to dark (a grey sheet
     lit from one side) scores low, and filling it from the edges would stop
     part-way and leave ragged light and dark patches. */
  const evenEdge = (data, width, height, bg, near) => {
    let even = 0; let total = 0;
    const check = (x, y) => {
      const i = (y * width + x) * 4;
      if (Math.max(Math.abs(data[i] - bg[0]), Math.abs(data[i + 1] - bg[1]), Math.abs(data[i + 2] - bg[2])) < near) even += 1;
      total += 1;
    };
    for (let x = 0; x < width; x += 1) { check(x, 0); check(x, height - 1); }
    for (let y = 1; y < height - 1; y += 1) { check(0, y); check(width - 1, y); }
    return even / total;
  };

  const NEUTRAL_NEAR = 10;

  const replaceNeutral = (data, width, height, bg) => {
    const near = NEUTRAL_NEAR; const far = 26;
    const shift = [TARGET[0] - bg[0], TARGET[1] - bg[1], TARGET[2] - bg[2]];
    const seen = new Uint8Array(width * height);
    const stack = new Int32Array(width * height);
    let top = 0;
    const push = (x, y) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const p = y * width + x;
      if (seen[p]) return;
      seen[p] = 1;
      stack[top] = p; top += 1;
    };
    for (let x = 0; x < width; x += 1) { push(x, 0); push(x, height - 1); }
    for (let y = 0; y < height; y += 1) { push(0, y); push(width - 1, y); }
    while (top > 0) {
      top -= 1;
      const p = stack[top];
      const i = p * 4;
      const d = Math.max(Math.abs(data[i] - bg[0]), Math.abs(data[i + 1] - bg[1]), Math.abs(data[i + 2] - bg[2]));
      if (d >= far) continue;
      const keep = d <= near ? 0 : (d - near) / (far - near);
      /* Move towards the target but never past it: a panel that already sits
         at the target inside a lighter border must not end up darker. */
      for (let c = 0; c < 3; c += 1) {
        const value = data[i + c];
        const moved = value + shift[c] * (1 - keep);
        if (shift[c] < 0) data[i + c] = value <= TARGET[c] ? value : Math.max(TARGET[c], moved);
        else data[i + c] = value >= TARGET[c] ? value : Math.min(TARGET[c], moved);
      }
      if (d < near) {
        const x = p % width; const y = (p - x) / width;
        push(x + 1, y); push(x - 1, y); push(x, y + 1); push(x, y - 1);
      }
    }
  };

  /* Returns true when the canvas now holds a repainted photo. */
  /* Work at the size the photo is shown, not the file's size: a card thumbnail
     needs a fraction of the pixels, and on a phone each full-size pass is a long task. */
  const sideFor = (img) => {
    if (img.closest('.product-gallery__slide')) return MAX_SIDE;
    const zoom = parseFloat(getComputedStyle(root).zoom) || 1; // big screens scale the page up
    const shown = Math.max(img.clientWidth, img.clientHeight) * zoom * (window.devicePixelRatio || 1);
    return shown > 0 ? Math.min(MAX_SIDE, Math.max(240, Math.ceil(shown * 1.25))) : MAX_SIDE;
  };

  const repaint = (img) => {
    const scale = Math.min(1, sideFor(img) / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);
    if (width < 8 || height < 8) return false;
    canvas.width = width; canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(img, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height);
    const bg = backdropOf(pixels.data, width, height);
    if (!bg || gap(bg, TARGET) < 3) return false;
    const spread = Math.max(bg[0], bg[1], bg[2]) - Math.min(bg[0], bg[1], bg[2]);
    if (spread >= 18) replaceColoured(pixels.data, bg, spread);
    else if (evenEdge(pixels.data, width, height, bg, NEUTRAL_NEAR) < 0.8) return false;
    else replaceNeutral(pixels.data, width, height, bg);
    context.putImageData(pixels, 0, 0);
    return true;
  };

  const queue = [];
  let busy = false;

  const next = () => {
    const img = queue.shift();
    if (!img) { busy = false; return; }
    busy = true;
    const finish = (state) => {
      img.dataset.backdrop = state;
      setTimeout(next, 0);
    };
    if (!img.isConnected || !img.naturalWidth) { finish('skip'); return; }
    let painted = false;
    try {
      painted = repaint(img);
    } catch (error) {
      finish('skip');
      return;
    }
    if (!painted) { finish('skip'); return; }
    canvas.toBlob((blob) => {
      if (!blob) { finish('skip'); return; }
      img.dataset.backdrop = 'done';
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = URL.createObjectURL(blob);
      setTimeout(next, 0);
    }, 'image/webp', 0.92);
  };

  /* Each repaint is a long task. While the page is still loading, photos below
     the fold wait for the load event so they do not hold up the first paint of
     what is on screen (the homepage hero on a phone). Photos in view go first. */
  let loaded = document.readyState === 'complete';
  const waiting = [];
  const offScreen = (img) => img.getBoundingClientRect().top > window.innerHeight;

  const consider = (img) => {
    if (!(img instanceof HTMLImageElement) || img.dataset.backdrop || !img.matches(SELECTOR)) return;
    if (!img.complete) return; // its load event brings it back here
    img.dataset.backdrop = 'queued';
    if (!loaded && offScreen(img)) { waiting.push(img); return; }
    queue.push(img);
    if (!busy) next();
  };

  if (!loaded) {
    window.addEventListener('load', () => {
      const release = () => {
        loaded = true;
        queue.push(...waiting.splice(0));
        if (!busy) next();
      };
      if ('requestIdleCallback' in window) requestIdleCallback(release, { timeout: 1500 });
      else setTimeout(release, 200);
    }, { once: true });
  }

  /* load does not bubble, so listen in the capture phase: this also catches
     photos added later (filters, quick view, search suggestions, the cart). */
  document.addEventListener('load', (event) => consider(event.target), true);
  document.addEventListener('error', (event) => {
    const img = event.target;
    if (img instanceof HTMLImageElement && img.matches(SELECTOR) && !img.dataset.backdrop) img.dataset.backdrop = 'skip';
  }, true);

  root.classList.add('photo-backdrop');
  document.querySelectorAll(SELECTOR).forEach(consider);
})();
